import { createRouter, createWebHistory, RouteLocationNormalized, Router } from 'vue-router';
import Store from '@monolith/legacy/store/index';
import { FeatureFlag, isEnabled } from '@monolith/legacy/services/features';
import { mapGetters } from 'vuex';
import {
  BoutiqueRoutes,
  CollectionRoutes,
  ConnectedHomeRoutes,
  createCategoryRoutes,
  discountsRoutes,
  searchRoutes,
  wholesaleRoutes,
} from '@bc/discovery';
import { CampaignsRoutes, NetworkRoutes, CartsToConvertRoutes } from '@bc/brands';
import { routes as OffersAndDiscountsRoutes } from '@bc/brands/domain/offers-and-discounts/pages/routes';
import { getBrandAccountSettingsRoutes } from '@monolith/legacy/modules/brand-account/brand-account.router';
import {
  accountFulfillmentGuard,
  accountIntegrationsGuard,
  accountSubscriptionGuard,
  brandCollectionGuard,
  brandDetailGuard,
  campaignDetailGuard,
  campaignGuard,
  campaignRecipientsGuard,
  changelogGuard,
  editPreparedOrderGuard,
  globalAfterEachGuard,
  globalBeforeEachGuard,
  isLoggedInGuard,
  landingSubscriptionProgramGuard,
  myListGuard,
  orderListGuard,
  paymentsGuard,
  preparedOrderGuard,
  productDetailGuard,
  smartReorderGuard,
} from '@core/routes/router-guard';
import {
  brandPropsParser,
  brandRegistrationPropsParser,
  productCollectionPropsParser,
  productPropsParser,
  paymentsPropsParser,
} from '@core/routes/router-util';
import * as AdsDashboardRoutes from '@bc/advertisement/feature/ads-dashboard/routes';
import i18n, { loadLanguageAsync } from '@monolith/legacy/services/i18n';
import { scrollBehavior } from '@core/routes/scroll-behaviour';
import { setupChunkLoadErrorHandler } from '@core/utilities/chunk-load-error-handler';

const Account = () => import('@monolith/legacy/components/account/account.vue');
const AccountOrdersRetailer = () => import('@monolith/legacy/components/account/orders/orders-view/retailer/orders-retailer.vue');
const AccountOrdersBrand = () => import('@monolith/legacy/components/account/orders/orders-view/brand/orders-brand.vue');
const AccountReorderSmart = () => import('@client/modules/smart-reorder/pages/smart-reorder-page.vue');
const WhatsNewPage = () => import('@client/modules/cms/changelog/pages/whats-new-page.vue');
const AccountReorderLegacy = () => import('@monolith/legacy/modules/reorder/pages/account-reorder.vue');

const AccountReorderItemList = () => import('@monolith/legacy/modules/reorder/pages/reorder-item-list.vue');
const AccountIntegrations = () => import('@monolith/legacy/components/account/account-integrations/account-integrations.vue');
const AccountIntegrationsDetail = () =>
  import('@monolith/legacy/components/account/account-integrations/private-apps/account-integrations-detail.vue');
const AccountIntegrationsListing = () =>
  import('@monolith/legacy/components/account/account-integrations/private-apps/account-integrations-listing.vue');
const AccountIntegrationsWebhooksDetail = () =>
  import('@monolith/legacy/components/account/account-integrations/private-apps/account-integrations-webhooks-detail.vue');
const AccountFavoriteBrands = () => import('@monolith/legacy/components/account/account-favorite-brands.vue');
const PublicIntegrationsInstall = () =>
  import('@monolith/legacy/components/account/account-integrations/public-apps/public-integrations-install/public-integrations-install.vue');
// Fulfillment
const AccountFulfillment = () => import('@monolith/legacy/components/account/account-fulfillment/account-fulfillment.vue');
const AccountFulfillmentInventory = () =>
  import('@monolith/legacy/components/account/account-fulfillment/fulfillment-inventory/account-fulfillment-inventory.vue');
const AccountFulfillmentLots = () =>
  import('@monolith/legacy/components/account/account-fulfillment/fulfillment-lots/account-fulfillment-lots.vue');
const AccountFulfillmentReplenishments = () =>
  import('@monolith/legacy/components/account/account-fulfillment/fulfillment-replenishments/account-fulfillment-replenishments.vue');
const AccountFulfillmentReplenishmentView = () =>
  import('@monolith/legacy/components/account/account-fulfillment/fulfillment-replenishments/account-fulfillment-replenishment-view.vue');
const AccountFulfillmentReplenishmentsCreateUpdate = () =>
  import('@monolith/legacy/components/account/account-fulfillment/fulfillment-replenishments/account-replenishments-create-update/account-fulfillment-replenishments-create-update.vue');
// Loyalty
const AccountOffersAndEvents = () =>
  import('@monolith/legacy/components/account/account-offers-events/account-offers-events.vue');
const AccountRepeatOffers = () =>
  import('@monolith/legacy/components/account/account-offers-events/repeat-offer/account-repeat-offers.vue');
const AccountEvent = () => import('@monolith/legacy/components/account/account-offers-events/events/account-event.vue');

const AccountExternalOrderCreate = () =>
  import('@monolith/legacy/components/account/orders/external-order-create/external-order-create.vue');

const AccountOrderEdit = () => import('@bc/account/order/pages/order-edit/order-edit.vue');
const AccountOrder = () => import('@monolith/legacy/components/account/orders/order-layout/order-layout.vue');
const AccountPayments = () => import('@monolith/legacy/components/account/account-payments/account-payments.vue');
const AccountPreparedOrdersList = () =>
  import('@monolith/legacy/components/account/prepared-orders/prepared-orders-list/account-prepared-orders-list.vue');

const AccountPreparedOrder = () =>
  import('@monolith/legacy/components/account/prepared-orders/brand-prepared-order-preview/prepared-order.vue');
const RetailerPreparedOrderPreview = () =>
  import('@monolith/legacy/components/account/prepared-orders/retailer-prepared-order-preview/retailer-prepared-order-preview.vue');
const RetailerSettings = () => import('@modules/account/retailer-settings/retailer-settings.vue');
const BrandSettingsPage = () => import('@monolith/legacy/modules/brand-account/pages/settings/settings.page.vue');
const AccountWallet = () => import('@monolith/legacy/components/account/account-wallet.vue');
const SuggestBrand = () => import('@monolith/legacy/components/account/account-suggest-brand.vue');
const CampaignListPage = () => import('@monolith/legacy/components/account/email-campaigns/campaign-list-page.vue');
const CampaignDetailPage = () => import('@monolith/legacy/components/account/email-campaigns/campaign-detail-page.vue');
const CampaignRecipientsPage = () => import('@monolith/legacy/components/account/email-campaigns/campaign-recipients-page.vue');
const CampaignPreferencesPage = () => import('@monolith/legacy/components/campaign-preferences/campaign-preferences-page.vue');
const AccountMessages = () => import('@monolith/legacy/components/account/message/message-list.vue');
const AccountConversations = () => import('@monolith/scopes/chat/components/account-conversations.vue');
const AccountMessagesDetail = () => import('@monolith/legacy/components/account/message/message-detail.vue');
const PublicIntegrationDetail = () =>
  import('@monolith/legacy/components/account/account-integrations/public-apps/public-integration-detail.vue');
const Brand = () => import('@monolith/legacy/components/brand/brand.vue');
const Product = () => import('@monolith/legacy/components/brand/product.vue');
const ProductCollection = () => import('@monolith/legacy/components/brand/product-collection.vue');
const MyList = () => import('@monolith/legacy/components/pages/my-list.vue');
const ReferralLandingPage = () => import('@monolith/legacy/components/pages/referral-landing-page.vue');
const RegisterPage = () => import('@monolith/legacy/components/sign-up/retailer/register-page.vue');
const ReviewForm = () => import('@monolith/legacy/components/review/review-form.vue');
const BrandOnboardingLogin = () =>
  import('@monolith/legacy/components/brand-registration/brand-onboarding/authentication/brand-onboarding-login.vue');
const BrandOnboardingSignup = () =>
  import('@monolith/legacy/components/brand-registration/brand-onboarding/authentication/brand-onboarding-signup.vue');
const BrandRegistrationView = () => import('@monolith/legacy/components/brand-registration/brand-registration-view.vue');
const BrandRegisterPage = () => import('@monolith/legacy/components/sign-up/brand/brand-register-page.vue');
const ReviewThankYouPage = () => import('@monolith/legacy/components/review/review-thank-you-page.vue');
const OrderPage = () => import('@monolith/legacy/components/account/orders/order-page.vue');
const CartPage = () => import('@monolith/legacy/modules/cart-2/pages/cart.vue');
const AccountOrderCreate = () =>
  import('@monolith/legacy/components/account/prepared-orders/create-prepared-order/order-create.vue');
const ProductDraftPreview = () => import('@monolith/legacy/components/preview/product-draft-preview.vue');
const CheckoutPage = () => import('@monolith/legacy/modules/cart-2/pages/checkout-page.vue');
const CheckoutChild = () => import('@monolith/legacy/modules/cart-2/pages/checkout-child.vue');
const PaymentChild = () => import('@monolith/legacy/modules/cart-2/pages/payment-child.vue');
const nafoCustomerCreate = () =>
  import('@monolith/legacy/components/account/orders/external-order-create/nafo-customer-create/nafo-customer-create.vue');

const PublicIntegrations = () =>
  import('@monolith/legacy/components/account/account-integrations/public-apps/public-integrations.vue');
const MyNetworkIntegrations = () =>
  import('@monolith/legacy/components/account/account-integrations/my-network/my-network-integrations.vue');
const ShippingIntegrations = () =>
  import('@monolith/legacy/components/account/account-integrations/shipping/shipping-integrations.vue');
const ReviewsBrand = () => import('@monolith/legacy/components/account/reviews/reviews-brand.vue');

const PageNotFound = () => import('@monolith/legacy/components/core/page-not-found/page-not-found.vue');
const CartNotFound = () => import('@monolith/legacy/components/order/cart-not-found.vue');

const SubscriptionProgramV2 = () =>
  import('@monolith/legacy/modules/promote-engage/subscription-program-v2/subscription-program.vue');
const AccountAnkorstorePlus = () =>
  import('@monolith/legacy/components/account/account-ankorstore-plus/account-ankorstore-plus.vue');

const homePage = () => import('@client/pages/home/index.vue');
const loginPage = () => import('@monolith/legacy/components/popins/auth-popin.vue');

const vueOptions = {};

let router: Router | undefined;

export function isBrandPath(path: string): boolean {
  return path && path.startsWith('/brand/');
}

function isBrandPathValid(path: string): boolean {
  return [/^\/brand\/[a-z\d-]+-[\d*]+(\/.*)?$/].some((route) => route.test(path));
}

export function isPathValid(path: string): boolean {
  switch (true) {
    case isBrandPath(path): {
      return isBrandPathValid(path);
    }
    case path === '/landing': {
      return false;
    }
    case path === '/': {
      return true;
    }
    default: {
      return true;
    }
  }
}

export function buildRoutes() {
  return [
    {
      path: '/account',
      component: Account,
      beforeEnter: [isLoggedInGuard],
      children: [
        ...AdsDashboardRoutes.routes,
        {
          path: 'checkouts',
          beforeEnter: () => {
            window.location.replace('/v2/account/checkouts');
            return false;
          },
        },
        {
          path: 'checkouts/:id',
          beforeEnter: () => {
            window.location.replace('/v2/account/checkouts');
            return false;
          },
        },
        {
          path: 'reorder',
          name: 'account-reorder',
          component: AccountReorderLegacy,
        },
        {
          path: 'smart-reorder',
          name: 'account-smart-reorder',
          component: AccountReorderSmart,
          beforeEnter: smartReorderGuard,
        },
        {
          path: 'reorder/:brandId',
          name: 'reorder-brand-detail',
          component: AccountReorderItemList,
        },
        {
          path: 'orders',
          name: 'order-list',
          component: Store.getters.userIsRetailer ? AccountOrdersRetailer : AccountOrdersBrand,
          beforeEnter: orderListGuard,
        },
        {
          path: 'orders/create',
          name: 'create-nafo-form',
          component: AccountExternalOrderCreate,
        },
        {
          path: 'orders/create/customer/:id?',
          name: 'create-nafo-customer-form',
          component: nafoCustomerCreate,
        },
        {
          path: 'orders/:id',
          component: OrderPage,
          children: [
            {
              path: '',
              name: 'order-detail',
              component: AccountOrder,
            },
            {
              path: 'track',
              name: 'order-tracking',
              component: AccountOrder,
            },
            {
              path: 'edit',
              name: 'order-edit',
              component: AccountOrderEdit,
            },
            {
              path: 'review/write',
              name: 'order-review-write',
              component: ReviewForm,
              props: (route: RouteLocationNormalized) => ({ overallRating: Number(route.query.overall) }),
            },
            {
              path: 'review/thank-you-page',
              name: 'order-review-thank-you-page',
              component: ReviewThankYouPage,
            },
          ],
        },
        {
          path: 'prepared-orders',
          name: 'account.prepared-orders',
          component: AccountPreparedOrdersList,
          beforeEnter: preparedOrderGuard,
        },
        {
          path: 'prepared-orders/create',
          name: 'account.prepared-orders.create',
          component: AccountOrderCreate,
          beforeEnter: preparedOrderGuard,
        },
        {
          path: 'prepared-orders/:id',
          name: 'account.prepared-orders.preview',
          component: Store.getters.userIsRetailer ? RetailerPreparedOrderPreview : AccountPreparedOrder,
        },
        {
          path: 'prepared-orders/:id/confirm',
          name: 'account.prepared-orders.confirm',
          component: RetailerPreparedOrderPreview,
        },
        {
          path: 'prepared-orders/:id/edit',
          name: 'account.prepared-orders.edit',
          component: AccountOrderCreate,
          beforeEnter: editPreparedOrderGuard,
        },
        {
          path: 'settings',
          name: 'settings',
          component: Store.getters.userIsRetailer ? RetailerSettings : BrandSettingsPage,
          children: getBrandAccountSettingsRoutes(),
        },
        {
          path: 'payments',
          name: 'payments',
          component: AccountPayments,
          props: { tab: 'orders' },
          beforeEnter: paymentsGuard,
        },
        {
          path: 'payments/:tab',
          name: 'payments/:tab',
          component: AccountPayments,
          props: paymentsPropsParser,
          beforeEnter: paymentsGuard,
        },
        {
          path: 'integrations',
          component: AccountIntegrations,
          children: [
            {
              path: '',
              name: 'integrations-listing',
              components: {
                privateTabRouter: AccountIntegrationsListing,
                publicTabRouter: PublicIntegrations,
                myNetworkTabRouter: MyNetworkIntegrations,
                shippingTabRouter: ShippingIntegrations,
              },
              beforeEnter: accountIntegrationsGuard,
            },
            {
              path: 'private/create',
              name: 'integrations-create',
              components: {
                privateTabRouter: AccountIntegrationsDetail,
              },
            },
            {
              path: 'private/edit/:id',
              name: 'integrations-edit',
              components: {
                privateTabRouter: AccountIntegrationsDetail,
              },
            },
            {
              path: 'private/edit/:id/subscriptions/create',
              name: 'integrations-subscriptions-create',
              components: {
                privateTabRouter: AccountIntegrationsWebhooksDetail,
              },
            },
            {
              path: 'private/edit/:id/subscriptions/create/:subscriptionId',
              name: 'integrations-subscriptions-edit',
              components: {
                privateTabRouter: AccountIntegrationsWebhooksDetail,
              },
            },
            {
              path: 'public/:name',
              name: 'public-integration-detail',
              components: {
                publicTabRouter: PublicIntegrationDetail,
              },
            },
            {
              path: 'public/:name/install',
              name: 'public-integration-install',
              components: {
                publicTabRouter: PublicIntegrationsInstall,
              },
            },
          ],
        },
        {
          path: 'fulfilment',
          component: AccountFulfillment,
          children: [
            {
              path: '',
              name: 'fulfillment',
              redirect: { name: 'fulfillment-inventory' },
              component: AccountFulfillmentInventory,
            },
            {
              path: 'items',
              name: 'fulfillment-inventory',
              component: AccountFulfillmentInventory,
            },
            {
              path: 'lots',
              name: 'fulfillment-lots',
              component: AccountFulfillmentLots,
            },
            {
              path: 'replenishments',
              name: 'fulfillment-replenishments',
              component: AccountFulfillmentReplenishments,
              beforeEnter: accountFulfillmentGuard,
            },
            {
              path: 'replenishments/create',
              name: 'fulfillment-replenishments-create',
              component: AccountFulfillmentReplenishmentsCreateUpdate,
            },
            {
              path: 'replenishments/:id',
              name: 'fulfillment-replenishment-view',
              component: AccountFulfillmentReplenishmentView,
            },
            {
              path: 'replenishments/:id/update',
              name: 'fulfillment-replenishment-update',
              component: AccountFulfillmentReplenishmentsCreateUpdate,
            },
            {
              path: 'orders/create',
              name: 'external-order-create',
              component: AccountExternalOrderCreate,
            },
          ],
        },
        {
          path: 'reviews-brand',
          name: 'reviews-brand',
          component: ReviewsBrand,
        },
        {
          path: 'favoriteBrands',
          name: 'favoriteBrands',
          component: AccountFavoriteBrands,
        },
        {
          path: 'wallet',
          name: 'wallet',
          component: AccountWallet,
        },
        {
          path: 'ankorstore-plus',
          name: 'ankorstore-plus',
          component: AccountAnkorstorePlus,
          beforeEnter: accountSubscriptionGuard,
        },
        {
          path: 'suggestBrand',
          name: 'suggestBrand',
          component: SuggestBrand,
        },
        {
          path: 'campaigns',
          name: 'campaigns',
          props: true,
          component: CampaignListPage,
          beforeEnter: campaignGuard,
        },
        {
          path: 'campaigns/campaign/:id',
          name: 'campaigns-detail',
          component: CampaignDetailPage,
          props: true,
          beforeEnter: campaignDetailGuard,
        },
        {
          path: 'campaigns/campaign/:id/recipients',
          name: 'campaigns-recipients',
          component: CampaignRecipientsPage,
          props: true,
          beforeEnter: campaignRecipientsGuard,
        },
        ...CampaignsRoutes,
        ...CartsToConvertRoutes,
        {
          path: 'messages',
          name: 'messages-list',
          component: isEnabled('chat-v2') ? AccountConversations : AccountMessages,
        },
        {
          path: 'messages/new',
          name: 'messages-new',
          component: AccountMessagesDetail,
        },
        {
          path: 'messages/:id',
          name: 'messages-detail',
          component: AccountMessagesDetail,
        },
        ...NetworkRoutes,
        ...OffersAndDiscountsRoutes,
        {
          path: 'download-accountancy-zip-files',
          name: 'download-accountancy-zip-files',
          children: [
            {
              path: 'show',
              name: 'download-accountancy-zip-files-show',
            },
            {
              path: ':file',
              name: 'download-accountancy-zip-files-download',
            },
          ],
        },
        {
          path: 'offers-and-events',
          name: 'offers-and-events',
          component: AccountOffersAndEvents,
        },
        {
          path: 'offers-and-events/repeat-offers',
          name: 'repeat-offers',
          component: AccountRepeatOffers,
        },
        {
          path: 'offers-and-events/event/:id',
          name: 'event',
          props: true,
          component: AccountEvent,
        },
      ],
    },
    {
      path: '/',
      name: 'home',
      component: homePage,
    },
    {
      path: '/ankorstore-plus-signup',
      name: 'ankorstore-plus-signup',
      component: SubscriptionProgramV2,
      beforeEnter: landingSubscriptionProgramGuard,
    },
    {
      name: 'lift_page',
      path: '/lift/:brand',
      component: ReferralLandingPage,
      props: brandPropsParser,
      beforeEnter: brandDetailGuard,
    },
    ...createCategoryRoutes(),
    ...searchRoutes,
    ...discountsRoutes,
    {
      name: 'brand-collection',
      path: '/brand/:brand/collections/:collectionSlug',
      component: ProductCollection,
      props: productCollectionPropsParser,
      beforeEnter: brandCollectionGuard,
    },
    {
      name: 'brand',
      path: '/brand/:brand',
      component: Brand,
      props: brandPropsParser,
      beforeEnter: brandDetailGuard,
      meta: {
        scrollContainer: '#catalog-hits-tiles-container',
      },
    },
    {
      name: 'product',
      path: '/brand/:brand/:product',
      component: Product,
      props: productPropsParser,
      beforeEnter: productDetailGuard,
    },
    {
      name: 'mylist',
      path: '/my-list',
      component: MyList,
      beforeEnter: [isLoggedInGuard, myListGuard],
    },
    {
      name: 'product-draft-preview',
      path: '/preview/product-drafts/:id',
      component: ProductDraftPreview,
    },
    {
      name: 'brand-registration',
      path: '/brand-registration',
      component: BrandRegisterPage,
      props: brandRegistrationPropsParser,
    },
    {
      name: 'register',
      path: '/register',
      component: RegisterPage,
    },
    {
      name: 'brand-onboarding',
      path: '/brand-onboarding',
      component: BrandRegistrationView,
      children: [
        {
          name: 'login',
          path: 'login',
          component: BrandOnboardingLogin,
        },
        {
          name: 'signup',
          path: 'signup',
          component: BrandOnboardingSignup,
        },
      ],
    },
    ...ConnectedHomeRoutes,
    {
      name: 'carts',
      path: '/carts',
      component: CartPage,
      beforeEnter: isLoggedInGuard,
    },
    {
      name: 'cart-not-found',
      path: '/cart-not-found',
      component: CartNotFound,
    },
    {
      path: '/checkout',
      component: CheckoutPage,
      async beforeEnter(to, from: RouteLocationNormalized, next) {
        to.params.retailerId = await Store.getters.retailer?.uuid;
        return isLoggedInGuard(to, from, next);
      },
      children: [
        {
          name: 'checkout',
          path: '',
          component: CheckoutChild,
          async beforeEnter(to, _from, next) {
            to.params.cartId = await Store.getters.retailer?.cart_uuid;
            next();
          },
        },
        {
          name: 'payment',
          path: 'payment/:cartId',
          component: PaymentChild,
          props: true,
        },
      ],
    },
    ...wholesaleRoutes,
    {
      name: 'campaign-preferences',
      path: '/campaign-preferences',
      component: CampaignPreferencesPage,
      props: true,
    },
    ...BoutiqueRoutes,
    ...CollectionRoutes,
    {
      path: '/whats-new',
      name: 'whats-new',
      component: WhatsNewPage,
      beforeEnter: changelogGuard,
    },
    {
      path: '/login',
      name: 'Login',
      component: loginPage,
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'PageNotFound',
      component: PageNotFound,
    },
  ];
}

export function initRouter(): void {
  if (isPathValid(globalThis.location.pathname)) {
    const routes = buildRoutes();

    router = createRouter({
      routes,
      scrollBehavior,
      history: createWebHistory('/'),
    });

    router.beforeEach((to, from) => globalBeforeEachGuard(to, from, router));

    router.afterEach(globalAfterEachGuard);

    // Handle chunk load errors (e.g., after deployment when cached HTML references old chunks)
    if (isEnabled(FeatureFlag.ChunkLoadErrorHandler)) {
      setupChunkLoadErrorHandler(router);
    }

    vueOptions.router = router;
    vueOptions.computed = {
      ...mapGetters(['user']),
    };
  } else {
    loadLanguage();
  }
}

async function loadLanguage() {
  const lang = document.documentElement.lang;
  if (lang) {
    await loadLanguageAsync(lang, i18n);
  }
}

export { vueOptions, router };
